import { WORKSPACE_STORAGE_TYPE } from '@bangle.io/constants';
import type { CollabStatus } from '@bangle.io/supabase';
import { WsPath } from '@bangle.io/ws-path';

export type NoteSaveStatusKind = 'clean' | 'pending' | 'failed';

export type NoteSavePillKind =
  | NoteSaveStatusKind
  | 'connecting'
  | 'disconnected'
  | 'offline'
  | 'error';

export type NoteSavePillModel = {
  kind: NoteSavePillKind;
  label: string;
  title: string;
  /** When true, append “· {location}” after the label (Saved only). */
  showLocation: boolean;
};

/** Short location phrase shown next to Saved (e.g. "in this browser"). */
export function getNoteSaveLocationLabel(
  storageType: string | undefined,
): string {
  switch (storageType) {
    case WORKSPACE_STORAGE_TYPE.Browser:
      return t.app.editor.saveStatus.locationBrowser;
    case WORKSPACE_STORAGE_TYPE.NativeFS:
      return t.app.editor.saveStatus.locationNativeFs;
    case WORKSPACE_STORAGE_TYPE.Supabase:
      return t.app.editor.saveStatus.locationCloud;
    default:
      return t.app.editor.saveStatus.locationOther;
  }
}

/** Tooltip explaining where durable writes land for this workspace type. */
export function getNoteSaveStatusTitle(
  kind: NoteSaveStatusKind,
  storageType: string | undefined,
): string {
  if (kind === 'pending') {
    return t.app.editor.saveStatus.savingTitle;
  }
  if (kind === 'failed') {
    return t.app.editor.saveStatus.failedTitle;
  }

  switch (storageType) {
    case WORKSPACE_STORAGE_TYPE.Browser:
      return t.app.editor.saveStatus.savedTitleBrowser;
    case WORKSPACE_STORAGE_TYPE.NativeFS:
      return t.app.editor.saveStatus.savedTitleNativeFs;
    case WORKSPACE_STORAGE_TYPE.Supabase:
      return t.app.editor.saveStatus.savedTitleCloud;
    default:
      return t.app.editor.saveStatus.savedTitleOther;
  }
}

/**
 * Merges the editor write queue with cloud collab transport status into one
 * pill model. Non-cloud workspaces ignore collab and keep the location suffix.
 */
export function resolveNoteSavePill(params: {
  editorStatus: NoteSaveStatusKind;
  collabStatus: CollabStatus | null;
  storageType: string | undefined;
}): NoteSavePillModel {
  const { editorStatus, collabStatus, storageType } = params;
  const isCloud = storageType === WORKSPACE_STORAGE_TYPE.Supabase;

  if (editorStatus === 'failed') {
    return {
      kind: 'failed',
      label: t.app.editor.saveStatus.saveFailed,
      title: t.app.editor.saveStatus.failedTitle,
      showLocation: false,
    };
  }

  if (isCloud && collabStatus === 'error') {
    return {
      kind: 'error',
      label: t.app.editor.saveStatus.connectionError,
      title: t.app.editor.saveStatus.connectionErrorTitle,
      showLocation: false,
    };
  }

  if (isCloud && collabStatus === 'offline') {
    return {
      kind: 'offline',
      label: t.app.editor.saveStatus.offline,
      title: t.app.editor.saveStatus.offlineTitle,
      showLocation: false,
    };
  }

  if (isCloud && collabStatus === 'disconnected') {
    return {
      kind: 'disconnected',
      label: t.app.editor.saveStatus.disconnected,
      title: t.app.editor.saveStatus.disconnectedTitle,
      showLocation: false,
    };
  }

  if (isCloud && collabStatus === 'connecting') {
    return {
      kind: 'connecting',
      label: t.app.editor.saveStatus.connecting,
      title: t.app.editor.saveStatus.connectingTitle,
      showLocation: false,
    };
  }

  // Only the durable Markdown queue drives "Saving…". Opportunistic Yjs
  // snapshot persists briefly set collabStatus to 'saving' after a clean
  // write and would otherwise flicker Saved → Saving → Saved.
  if (editorStatus === 'pending') {
    return {
      kind: 'pending',
      label: t.app.editor.saveStatus.saving,
      title: t.app.editor.saveStatus.savingTitle,
      showLocation: false,
    };
  }

  return {
    kind: 'clean',
    label: t.app.editor.saveStatus.saved,
    title: getNoteSaveStatusTitle('clean', storageType),
    showLocation: true,
  };
}

export function noteNameFromWsPath(wsPath: string): string {
  const file = WsPath.fromString(wsPath).asFile();
  if (!file) {
    return wsPath;
  }
  return file.fileNameWithoutExtension;
}
